import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, BadgeCheck, MapPin, Calendar, Coins, Pencil, ShieldAlert, Plus,
  LogOut, X, Loader2, Save, Globe, Users, Bell, Images, UserPlus, Lock, Settings,
  Camera, Aperture, VenetianMask, Monitor, Heart, Sparkles,
} from "lucide-react";
import { useSession, TIER_META } from "@/context/SessionContext";
import { socialApi, resolveMedia } from "@/api/social";
import { messagingApi } from "@/api/messaging";
import PostComposerModal from "@/components/social/PostComposerModal";
import VaultPickButton from "@/components/mist/VaultPickButton";
import ProfileFeed from "@/components/profile/ProfileFeed";
import MistGallery from "@/components/profile/MistGallery";
import SocialDirectory from "@/components/social/SocialDirectory";
import FollowersList from "@/components/social/FollowersList";
import NotificationsCenter from "@/components/social/NotificationsCenter";
import DefaultAvatar from "@/components/social/DefaultAvatar";
import SecurityHealthBadge from "@/components/profile/SecurityHealthBadge";
import { DECK_OPTIONS } from "@/components/dashboard/TopDeck";
import AdminPortal from "@/components/dashboard/AdminPortal";

const TIERS = ["tier_1", "tier_2", "tier_3"];
const CREATOR_TIERS = new Set(["tier_2", "tier_3"]);
const PRIVACY_OPTS = [
  { v: "public", label: "Public" },
  { v: "subscribers_only", label: "Subscribers" },
  { v: "private", label: "Private" },
];
const GENDERS = ["Male", "Female", "Non-Binary", "Prefer Not to Say"];
const RELATIONSHIP_OPTS = ["Single", "Married", "In a Relationship", "Prefer not to say"];

function TierBadge({ tier }) {
  const meta = TIER_META[tier] || TIER_META.tier_1;
  const base = "inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-1";
  if (meta.kind === "business")
    return <span data-testid="tier-badge" className={`${base} rounded-lg bg-charcoal text-gold border-2 border-gold shadow-[3px_3px_0px_#D4AF37]`}>{meta.label}</span>;
  if (meta.kind === "creator")
    return <span data-testid="tier-badge" className={`${base} rounded-full bg-gold text-black border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A]`}>{meta.label}<BadgeCheck size={14} strokeWidth={2.6} /></span>;
  return <span data-testid="tier-badge" className={`${base} rounded-full border-2 border-charcoal/40 text-charcoal/70`}>{meta.label}</span>;
}

function InfoPill({ icon: Icon, children, testid }) {
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 rounded-full border-2 border-charcoal bg-white px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-charcoal shadow-[2px_2px_0px_#1A1A1A]">
      <Icon size={12} strokeWidth={2.4} className="text-gold" />{children}
    </span>
  );
}

function MacroButton({ icon: Icon, label, onClick, testid, accent }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={[
        "flex flex-col items-center justify-center gap-1 rounded-2xl border-2 border-charcoal py-2.5 shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-[transform,box-shadow] duration-150",
        accent ? "bg-gold text-black" : "bg-white text-charcoal",
      ].join(" ")}>
      <Icon size={17} strokeWidth={2.3} />
      <span className="font-mono text-[9px] font-black uppercase tracking-[0.14em]">{label}</span>
    </button>
  );
}

export default function ProfilePage({ onNavigate, onNavVisibility }) {
  const { user, wallet, tier, switchTier, logout, setUser, setBiometric, token } = useSession();
  const jimAllowed = ["djohnm1972@gmail.com"].includes((user?.email || "").toLowerCase())
    || ["founder", "admin"].includes(user?.role);
  const [adminOpen, setAdminOpen] = useState(false);
  const activeTier = wallet?.subscription_tier || user?.subscription_tier || tier;
  const balance = wallet?.balance;
  const identityVerified = !!(user?.identity_verified || user?.verified);
  const isCreator = CREATOR_TIERS.has(activeTier);

  const [blocker, setBlocker] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", bio: "", location: "", language: "", gender: "", birth_date: "", top_deck_mode: "reels" });
  const [privacy, setPrivacy] = useState(user?.privacy || "public");
  const [followers, setFollowers] = useState(null);
  const [composer, setComposer] = useState(false);
  const [feedKey, setFeedKey] = useState(0);
  const [feedOpen, setFeedOpen] = useState(true);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [friendsOpen, setFriendsOpen] = useState(false);
  const [followersOpen, setFollowersOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  // media editing
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [bannerBusy, setBannerBusy] = useState(false);
  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  // biometric verification
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [camError, setCamError] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const lastScroll = useRef(0);

  useEffect(() => {
    socialApi.followersCount().then((r) => setFollowers(r.count)).catch(() => setFollowers(0));
  }, [user?.user_id]);

  // Attach the live camera stream whenever the verification overlay opens.
  useEffect(() => {
    if (!verifyOpen) return;
    let cancelled = false;
    setCamError(false);
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); }
      } catch { setCamError(true); }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [verifyOpen]);

  const onScroll = (e) => {
    const y = e.currentTarget.scrollTop;
    const prev = lastScroll.current;
    if (y > prev + 6 && y > 40) onNavVisibility?.(true);
    else if (y < prev - 6) onNavVisibility?.(false);
    lastScroll.current = y;
  };

  const openDM = (person) => {
    if (!person?.id) return;
    setFriendsOpen(false);
    setFollowersOpen(false);
    // Route into the Misteck Messenger (opens the installed PWA on this device
    // if present, otherwise the messenger in-browser). Messaging stays gated by
    // mutual friendship server-side.
    window.location.href = `/messenger?dm=${encodeURIComponent(person.id)}&space=private`;
  };

  const openEditor = () => {
    setForm({
      name: user?.name || "", bio: user?.bio || "",
      location: user?.location || "", language: user?.language || "English",
      gender: user?.gender || "", birth_date: user?.birth_date || "",
      top_deck_mode: user?.top_deck_mode || "reels",
      preferred_age_min: user?.preferred_age_min ?? "",
      preferred_age_max: user?.preferred_age_max ?? "",
      relationship_status: user?.relationship_status || "",
      interests: (user?.interests || []).join(", "),
      dislikes: (user?.dislikes || []).join(", "),
    });
    setPrivacy(user?.privacy || "public");
    setEditing(true);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, bio: form.bio, location: form.location,
        language: form.language, gender: form.gender, birth_date: form.birth_date,
        top_deck_mode: form.top_deck_mode,
        relationship_status: form.relationship_status,
        interests: (form.interests || "").split(",").map((s) => s.trim()).filter(Boolean),
        dislikes: (form.dislikes || "").split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (form.preferred_age_min !== "" && form.preferred_age_min != null) payload.preferred_age_min = Number(form.preferred_age_min);
      if (form.preferred_age_max !== "" && form.preferred_age_max != null) payload.preferred_age_max = Number(form.preferred_age_max);
      const { user: updated } = await socialApi.updateProfile(payload);
      if (isCreator && privacy !== (user?.privacy || "public")) {
        await socialApi.setPrivacy(privacy);
        updated.privacy = privacy;
      }
      setUser((prev) => ({ ...prev, ...updated }));
      toast.success("Profile updated");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile");
    } finally { setSaving(false); }
  };

  // ---- in-place avatar / cover editing ----
  const onMediaSelected = async (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const preview = URL.createObjectURL(file);
    const setBusy = kind === "avatar" ? setAvatarBusy : setBannerBusy;
    const setPreview = kind === "avatar" ? setAvatarPreview : setBannerPreview;
    setPreview(preview);
    setBusy(true);
    try {
      const res = await socialApi.upload(file);
      const field = kind === "avatar" ? "avatar_url" : "banner_url";
      const { user: updated } = await socialApi.updateProfile({ [field]: `/api/social/media/${res.id}` });
      setUser((prev) => ({ ...prev, ...updated }));
      toast.success(`${kind === "avatar" ? "Avatar" : "Cover"} updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
      setPreview(null);
    } finally { setBusy(false); }
  };

  // ---- biometric camera verification loop ----
  const snap = async () => {
    setVerifying(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 320; canvas.height = 320;
      const ctx = canvas.getContext("2d");
      const v = videoRef.current;
      if (v && v.videoWidth) {
        const s = Math.min(v.videoWidth, v.videoHeight);
        ctx.drawImage(v, (v.videoWidth - s) / 2, (v.videoHeight - s) / 2, s, s, 0, 0, 320, 320);
      } else {
        ctx.fillStyle = "#0A0A0A"; ctx.fillRect(0, 0, 320, 320);
        ctx.fillStyle = "#FFD028"; ctx.font = "bold 18px monospace";
        ctx.fillText("BIOMETRIC OK", 60, 165);
      }
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      const file = new File([blob], "biometric.png", { type: "image/png" });
      const res = await socialApi.upload(file);
      await setBiometric(true, res.id);
      toast.success("Identity verified");
      setVerifyOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setVerifying(false); }
  };

  const onWalletTap = () => {
    if (!identityVerified) { setBlocker(true); return; }
    toast("Coin top-up — coming soon", { icon: "🪙" });
  };

  const avatarSrc = avatarPreview || resolveMedia(user?.avatar_url, token);
  const bannerSrc = bannerPreview || resolveMedia(user?.banner_url, token);

  return (
    <div className="h-full w-full overflow-hidden flex flex-col bg-cream relative" data-testid="profile-page">
      {friendsOpen && (
        <div className="absolute inset-0 z-[65] bg-cream" data-testid="friends-overlay">
          <SocialDirectory onBack={() => setFriendsOpen(false)} onMessage={openDM} />
        </div>
      )}
      {followersOpen && (
        <div className="absolute inset-0 z-[65] bg-cream" data-testid="followers-overlay">
          <FollowersList onBack={() => setFollowersOpen(false)} onMessage={openDM} />
        </div>
      )}
      {adminOpen && (
        <div className="absolute inset-0 z-[75] bg-cream" data-testid="profile-admin-overlay">
          <AdminPortal onBack={() => setAdminOpen(false)} />
        </div>
      )}
      {notifOpen && (
        <div className="absolute inset-0 z-[65] bg-cream" data-testid="notif-overlay">
          <NotificationsCenter onClose={() => setNotifOpen(false)} onNavigate={(v) => { setNotifOpen(false); onNavigate?.(v); }} />
        </div>
      )}
      {galleryOpen && <MistGallery onClose={() => setGalleryOpen(false)} />}
      {composer && (
        <PostComposerModal onClose={() => setComposer(false)} onPosted={() => { setFeedOpen(true); setFeedKey((k) => k + 1); }} />
      )}

      {/* Live biometric camera verification overlay */}
      {verifyOpen && (
        <div className="absolute inset-0 z-[70] bg-black/90 flex flex-col items-center justify-center p-6" data-testid="biometric-camera-modal">
          <button onClick={() => setVerifyOpen(false)} data-testid="biometric-cancel-btn" className="absolute top-4 right-4 w-9 h-9 grid place-items-center rounded-full border-2 border-white/40 text-white"><X size={16} /></button>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-4">Identity Verification</p>
          <div className="relative w-56 h-56 rounded-full overflow-hidden border-4 border-gold shadow-[0_0_30px_rgba(255,208,40,0.5)] bg-charcoal grid place-items-center">
            <video ref={videoRef} muted playsInline className="w-full h-full object-cover" data-testid="biometric-video" />
            {camError && (
              <div className="absolute inset-0 grid place-items-center text-center px-4 bg-charcoal">
                <p className="font-mono text-[10px] text-white/70 leading-relaxed">Camera unavailable — you can still submit a verification snapshot.</p>
              </div>
            )}
          </div>
          <button onClick={snap} disabled={verifying} data-testid="biometric-snap-btn" className="mt-6 flex items-center gap-2 bg-gold text-black font-black font-mono text-xs uppercase tracking-widest px-6 py-3.5 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-70">
            {verifying ? <Loader2 size={16} className="animate-spin" /> : <Aperture size={16} />} Snap Verification Photo
          </button>
        </div>
      )}

      {blocker && (
        <div className="absolute inset-0 z-50 bg-black/85 flex items-center justify-center p-4" data-testid="unverified-blocker">
          <div className="w-[328px] max-w-[calc(100vw-32px)] bg-cream border-4 border-charcoal rounded-2xl p-5 shadow-hard text-center space-y-4">
            <div className="w-11 h-11 mx-auto rounded-full bg-red-100 text-red-600 border-2 border-red-600 grid place-items-center shadow-[2px_2px_0px_#1A1A1A]"><ShieldAlert size={22} /></div>
            <p className="font-mono text-[11px] leading-relaxed text-charcoal/80 bg-red-50 border-2 border-dashed border-red-200 rounded-xl p-2.5">UNVERIFIED: Please verify via the profile edit verification process. Unverified nodes cannot buy coin pools or deploy custom generation tasks.</p>
            <button data-testid="blocker-verify-btn" onClick={() => { setBlocker(false); setVerifyOpen(true); }} className="w-full bg-gold text-black font-black text-xs py-3 rounded-xl uppercase tracking-widest border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A]">Verify Now</button>
            <button data-testid="blocker-dismiss-btn" onClick={() => setBlocker(false)} className="w-full bg-charcoal text-white font-black text-xs py-3 rounded-xl uppercase tracking-widest">Return to Basic Browsing</button>
          </div>
        </div>
      )}

      {/* Editor */}
      {editing && (
        <div className="absolute inset-0 z-50 bg-black/70 flex items-end justify-center" data-testid="profile-editor">
          <div className="w-full max-w-[412px] bg-cream border-t-4 border-charcoal rounded-t-3xl p-5 shadow-hard space-y-3 max-h-[92dvh] overflow-y-auto no-scrollbar">
            <div className="flex items-center gap-2 pb-2 border-b-2 border-charcoal/10">
              <Pencil size={16} className="text-gold" />
              <h3 className="font-serif text-xl text-charcoal leading-none flex-1">Edit Profile</h3>
              <button onClick={() => setEditing(false)} data-testid="editor-close-btn" className="w-8 h-8 grid place-items-center rounded-lg border-2 border-charcoal bg-white shadow-[1.5px_1.5px_0px_#1A1A1A]"><X size={15} strokeWidth={2.6} /></button>
            </div>
            {[
              { key: "name", label: "1 · Display Name", icon: null },
              { key: "location", label: "2 · Location", icon: MapPin },
              { key: "language", label: "3 · Language", icon: Globe },
            ].map((f) => (
              <label key={f.key} className="block">
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1">{f.icon && <f.icon size={11} className="text-gold" />}{f.label}</span>
                <input value={form[f.key]} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} data-testid={`editor-field-${f.key}`} className="mt-1 w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs text-charcoal outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A]" />
              </label>
            ))}
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50">4 · Bio</span>
              <textarea value={form.bio} rows={3} onChange={(e) => setForm((s) => ({ ...s, bio: e.target.value }))} data-testid="editor-field-bio" className="mt-1 w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs text-charcoal outline-none focus:ring-2 focus:ring-gold resize-none shadow-[2px_2px_0px_#1A1A1A]" />
            </label>

            {/* 5 · Date of Birth */}
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><Calendar size={11} className="text-gold" />5 · Date of Birth</span>
              <input type="date" value={form.birth_date || ""} onChange={(e) => setForm((s) => ({ ...s, birth_date: e.target.value }))} data-testid="editor-field-birth_date" className="mt-1 w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs text-charcoal outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A]" />
            </label>

            {/* 6 · Gender */}
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><VenetianMask size={11} className="text-gold" />6 · Gender</span>
              <select value={form.gender || ""} onChange={(e) => setForm((s) => ({ ...s, gender: e.target.value }))} data-testid="editor-field-gender" className="mt-1 w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs text-charcoal outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A]">
                <option value="">Select…</option>
                {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </label>

            {/* 7 · Preferred Age Range — who can discover you in search */}
            <div className="block" data-testid="editor-age-range">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><Calendar size={11} className="text-gold" />7 · Preferred Age Range</span>
              <p className="font-mono text-[8px] text-charcoal/40 mt-0.5 leading-snug">Only people whose age is in this range can find you in search. Leave empty to be found by everyone.</p>
              <div className="mt-1.5 flex items-center gap-2">
                <input type="number" min="18" max="120" value={form.preferred_age_min} onChange={(e) => setForm((s) => ({ ...s, preferred_age_min: e.target.value }))} data-testid="editor-field-preferred_age_min" placeholder="Min" className="w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs text-charcoal outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A]" />
                <span className="font-mono text-charcoal/40 text-xs">—</span>
                <input type="number" min="18" max="120" value={form.preferred_age_max} onChange={(e) => setForm((s) => ({ ...s, preferred_age_max: e.target.value }))} data-testid="editor-field-preferred_age_max" placeholder="Max" className="w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs text-charcoal outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A]" />
              </div>
            </div>

            {/* 8 · Relationship Status */}
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><Heart size={11} className="text-gold" />8 · Relationship Status</span>
              <select value={form.relationship_status || ""} onChange={(e) => setForm((s) => ({ ...s, relationship_status: e.target.value }))} data-testid="editor-field-relationship_status" className="mt-1 w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs text-charcoal outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A]">
                <option value="">Select…</option>
                {RELATIONSHIP_OPTS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>

            {/* 9 · Interests / Likes */}
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><Sparkles size={11} className="text-gold" />9 · Likes & Interests</span>
              <p className="font-mono text-[8px] text-charcoal/40 mt-0.5">Comma-separated — helps match you with reels & people.</p>
              <input value={form.interests} onChange={(e) => setForm((s) => ({ ...s, interests: e.target.value }))} data-testid="editor-field-interests" placeholder="music, hiking, coding" className="mt-1 w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs text-charcoal outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A]" />
            </label>

            {/* 10 · Dislikes */}
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><Sparkles size={11} className="text-gold" />10 · Dislikes</span>
              <input value={form.dislikes} onChange={(e) => setForm((s) => ({ ...s, dislikes: e.target.value }))} data-testid="editor-field-dislikes" placeholder="spam, loud noises" className="mt-1 w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs text-charcoal outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A]" />
            </label>

            {/* 11 · Top Monitor Deck — hot-swaps the dashboard's top window */}
            <div className="block" data-testid="editor-deck-selector">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><Monitor size={11} className="text-gold" />11 · Top Monitor Deck</span>
              <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                {DECK_OPTIONS.map((d) => {
                  const active = form.top_deck_mode === d.mode;
                  return (
                    <button key={d.mode} type="button" onClick={() => setForm((s) => ({ ...s, top_deck_mode: d.mode }))} data-testid={`deck-opt-${d.mode}`}
                      className={[
                        "flex flex-col items-center justify-center gap-1 rounded-xl border-2 py-2 transition-colors duration-150",
                        active ? "bg-charcoal text-gold border-gold" : "bg-white text-charcoal/60 border-charcoal/30",
                      ].join(" ")}>
                      <d.Icon size={15} strokeWidth={2.3} />
                      <span className="font-mono text-[8px] font-black uppercase tracking-[0.1em]">{d.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Item #8 — deep account settings */}
            <div className="pt-2 border-t-2 border-charcoal/10" data-testid="editor-settings">
              <div className="flex items-center gap-1.5 mb-2">
                <Settings size={12} className="text-gold" />
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50">12 · Account Settings</span>
              </div>
              <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-charcoal/40 mb-1.5">Profile Visibility</p>
              <div className="grid grid-cols-3 gap-1.5">
                {PRIVACY_OPTS.map((o) => {
                  const disabled = o.v !== "public" && !isCreator;
                  const active = privacy === o.v;
                  return (
                    <button key={o.v} disabled={disabled} onClick={() => setPrivacy(o.v)} data-testid={`privacy-${o.v}`}
                      className={[
                        "font-mono text-[9px] uppercase tracking-[0.1em] rounded-lg border-2 py-2 transition-colors duration-150",
                        active ? "bg-charcoal text-gold border-gold" : "bg-white text-charcoal/60 border-charcoal/30",
                        disabled ? "opacity-40" : "",
                      ].join(" ")}>{o.label}</button>
                  );
                })}
              </div>
              {!isCreator && <p className="mt-1.5 font-mono text-[8px] text-charcoal/40 leading-snug">Only Creator / Business tiers can restrict visibility.</p>}
              <button onClick={logout} data-testid="editor-logout-btn" className="mt-3 w-full flex items-center justify-center gap-2 bg-red-600 border-2 border-charcoal text-white rounded-xl py-2.5 font-mono text-[10px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#1A1A1A]"><LogOut size={13} /> Log Out</button>
            </div>

            <button onClick={saveProfile} disabled={saving} data-testid="editor-save-btn" className="w-full bg-gold text-black font-black font-mono text-xs uppercase tracking-widest py-3 rounded-xl border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-[transform,box-shadow] duration-150 disabled:opacity-70 flex items-center justify-center gap-2">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}{saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="shrink-0 px-4 pt-3 pb-2 flex items-center gap-3">
        <button onClick={() => onNavigate?.("command")} data-testid="profile-back-btn" aria-label="Back" className="w-9 h-9 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={16} strokeWidth={2.4} /></button>
        <div className="leading-none flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">Identity Layer</p>
          <h1 className="font-serif text-2xl text-charcoal">My Profile</h1>
        </div>
        <button onClick={logout} data-testid="logout-btn" aria-label="Log out" className="w-9 h-9 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><LogOut size={15} strokeWidth={2.4} /></button>
      </header>

      {/* TOP ANTI-FRAUD BANNER — click to launch the camera verification loop */}
      {!identityVerified && (
        <button onClick={() => setVerifyOpen(true)} data-testid="identity-verify-banner" className="shrink-0 mx-4 mb-1 flex items-center gap-2 bg-red-600 text-white border-2 border-charcoal rounded-xl px-3 py-2 shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <X size={15} strokeWidth={3} className="shrink-0" />
          <span className="font-mono text-[10px] font-black uppercase tracking-[0.14em] flex-1 text-left leading-tight">Unverified · Tap to verify identity & unlock coins</span>
          <Camera size={16} className="shrink-0" />
        </button>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-6" onScroll={onScroll}>
        {/* Security & Rewards Health trust strip — taps through to Wealth rewards */}
        <SecurityHealthBadge onOpenWealth={() => {
          const p = new URLSearchParams(window.location.search);
          p.set("life", "wealth");
          p.set("tab", "command");
          window.history.pushState({}, "", `${window.location.pathname}?${p}`);
          onNavigate?.("command");
        }} />

        {/* hidden file hooks */}
        <input ref={avatarInputRef} type="file" accept="image/*" hidden onChange={(e) => onMediaSelected(e, "avatar")} data-testid="avatar-file-input" />
        <input ref={bannerInputRef} type="file" accept="image/*" hidden onChange={(e) => onMediaSelected(e, "banner")} data-testid="banner-file-input" />

        {/* Set avatar / banner straight from the media Vault (device OR vault everywhere) */}
        <div className="flex items-center justify-center gap-2 mb-3">
          <VaultPickButton onFile={(f) => onMediaSelected({ target: { files: [f], value: "" } }, "avatar")} bucket="images" testid="avatar-vault" label="Avatar from Vault"
            className="flex items-center gap-1.5 bg-charcoal text-[#00F5D4] border-2 border-charcoal rounded-lg px-3 py-1.5 font-mono text-[9px] font-black uppercase tracking-wider shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none" />
          <VaultPickButton onFile={(f) => onMediaSelected({ target: { files: [f], value: "" } }, "banner")} bucket="images" testid="banner-vault" label="Banner from Vault"
            className="flex items-center gap-1.5 bg-charcoal text-[#00F5D4] border-2 border-charcoal rounded-lg px-3 py-1.5 font-mono text-[9px] font-black uppercase tracking-wider shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none" />
        </div>

        {/* FIRST CONTAINER — public identity card */}
        <section className="border-4 border-charcoal rounded-[2rem] bg-white overflow-hidden shadow-hard" data-testid="identity-card">
          {/* Cover banner — tap to change */}
          <button type="button" onClick={() => bannerInputRef.current?.click()} data-testid="banner-edit-trigger" className="relative h-28 w-full border-b-4 border-charcoal bg-charcoal block group cursor-pointer">
            {bannerSrc && <img src={bannerSrc} alt="cover" className="w-full h-full object-cover" data-testid="profile-banner" />}
            <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-200 grid place-items-center">
              {bannerBusy ? <Loader2 size={22} className="text-white animate-spin" /> : <Camera size={22} className="text-white" />}
            </span>
            {/* Avatar — tap to change */}
            <span onClick={(e) => { e.stopPropagation(); avatarInputRef.current?.click(); }} data-testid="avatar-edit-trigger" className="absolute -bottom-11 left-1/2 -translate-x-1/2 group/av cursor-pointer">
              <span className="block w-24 h-24 rounded-full border-4 border-charcoal bg-cream overflow-hidden shadow-[4px_4px_0px_#1A1A1A] relative" data-testid="profile-avatar">
                {avatarSrc ? <img src={avatarSrc} alt="avatar" className="w-full h-full object-cover" /> : <DefaultAvatar name={user?.name} gender={user?.gender} />}
                <span className="absolute inset-0 bg-black/40 opacity-0 group-hover/av:opacity-100 group-active/av:opacity-100 transition-opacity duration-200 grid place-items-center">
                  {avatarBusy ? <Loader2 size={18} className="text-white animate-spin" /> : <Camera size={18} className="text-white" />}
                </span>
              </span>
            </span>
          </button>
          <div className="pt-14 px-4 pb-5 text-center">
            <div className="flex items-center justify-center gap-1.5">
              <h2 className="font-serif text-2xl text-charcoal leading-none" data-testid="profile-name">{user?.name || "—"}</h2>
              {identityVerified && <BadgeCheck size={20} strokeWidth={2.4} className="text-emerald-500" data-testid="verified-check" />}
            </div>
            <p className="font-mono text-xs text-charcoal/45 mt-1" data-testid="profile-handle">@{user?.handle || user?.user_id}</p>
            {user?.temporal_id && (
              <div className="mt-2 flex justify-center" data-testid="temporal-id">
                <span className="inline-flex items-center gap-1.5 rounded-full border-2 border-charcoal/25 bg-charcoal/5 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-charcoal/60">Tracking ID · {user.temporal_id}</span>
              </div>
            )}
            {!identityVerified && (
              <div className="mt-2 flex justify-center" data-testid="unverified-badge">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 text-white border-2 border-charcoal px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] shadow-[2px_2px_0px_#1A1A1A]"><X size={13} strokeWidth={3} /> Unverified</span>
              </div>
            )}
            <div className="mt-3 flex justify-center"><TierBadge tier={activeTier} /></div>
            <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
              {user?.location && <InfoPill icon={MapPin} testid="pill-location">{user.location}</InfoPill>}
              {user?.age != null && <InfoPill icon={Calendar} testid="pill-age">{user.age} yrs</InfoPill>}
              {user?.gender && <InfoPill icon={VenetianMask} testid="pill-gender">{user.gender}</InfoPill>}
              {user?.relationship_status && <InfoPill icon={Heart} testid="pill-relationship">{user.relationship_status}</InfoPill>}
            </div>
            {user?.bio && <p className="mt-3 font-mono text-[12px] text-charcoal/70 leading-relaxed px-2" data-testid="profile-bio">{user.bio}</p>}
            {user?.interests?.length > 0 && (
              <div className="mt-3 flex flex-wrap justify-center gap-1.5" data-testid="profile-interests">
                {user.interests.slice(0, 12).map((t) => (
                  <span key={t} className="rounded-full border-2 border-charcoal bg-gold/20 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-charcoal">{t}</span>
                ))}
              </div>
            )}

            {/* Dynamic self-view CTA — Followers [X] */}
            <button type="button" data-testid="profile-cta-followers" onClick={() => setFollowersOpen(true)}
              className="mt-4 w-full rounded-2xl border-2 border-charcoal bg-charcoal text-white px-4 py-2.5 flex items-center justify-center gap-2 font-mono text-[11px] font-black uppercase tracking-[0.18em] shadow-[3px_3px_0px_#D4AF37] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-[transform,box-shadow] duration-150">
              <Users size={15} className="text-gold" /> Followers
              <span className="text-gold tabular-nums" data-testid="followers-count">[{followers == null ? "…" : followers}]</span>
            </button>

            {/* MiLink balance — wallet frozen if unverified */}
            <button type="button" onClick={onWalletTap} className="mt-3 w-full rounded-2xl border-2 border-charcoal bg-cream px-4 py-3 flex items-center gap-3 shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none text-left" data-testid="milink-balance-card">
              <span className="w-9 h-9 rounded-xl border-2 border-gold text-gold grid place-items-center shrink-0"><Coins size={18} strokeWidth={2.2} /></span>
              <div className="text-left min-w-0 flex-1">
                <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-charcoal/50">MiLink Balance</p>
                <p className="font-mono text-xl font-bold text-charcoal tabular-nums truncate" data-testid="milink-balance-value">{balance == null ? "—" : Math.round(balance).toLocaleString()}</p>
              </div>
              <span className="w-8 h-8 rounded-lg border-2 border-charcoal bg-gold text-black grid place-items-center shrink-0 shadow-[2px_2px_0px_#1A1A1A]" data-testid="milink-topup-btn"><Plus size={16} strokeWidth={2.6} /></span>
            </button>
          </div>
        </section>

        {/* ===== SECURITY / PRIVACY FIREWALL GATE LINE ===== */}
        <div className="mt-4 flex items-center gap-2" data-testid="firewall-gate">
          <span className="h-[3px] flex-1 bg-[repeating-linear-gradient(90deg,#C05621_0_10px,transparent_10px_18px)]" />
          <span className="inline-flex items-center gap-1.5 rounded-full border-2 border-charcoal bg-white px-3 py-1 font-mono text-[8px] font-black uppercase tracking-[0.16em] text-charcoal shadow-[2px_2px_0px_#1A1A1A]">
            <Lock size={11} className="text-[#C05621]" /> Private · Friends & Subscribers
          </span>
          <span className="h-[3px] flex-1 bg-[repeating-linear-gradient(90deg,#C05621_0_10px,transparent_10px_18px)]" />
        </div>

        {/* SECOND CONTAINER — staggered macro matrix */}
        <section className="mt-4 border-2 border-charcoal rounded-2xl bg-white/60 p-2.5 shadow-[3px_3px_0px_#1A1A1A] space-y-2.5" data-testid="macro-matrix">
          <div className="grid grid-cols-3 gap-2.5">
            <MacroButton icon={Pencil} label="Edit" onClick={openEditor} testid="macro-edit" accent />
            <MacroButton icon={UserPlus} label="Follow" onClick={() => onNavigate?.("chat")} testid="macro-follow" />
            <MacroButton icon={Bell} label="Notif" onClick={() => setNotifOpen(true)} testid="macro-notifications" />
          </div>
          <div className="grid grid-cols-2 gap-2.5 px-6">
            <MacroButton icon={Images} label="Gallery" onClick={() => setGalleryOpen(true)} testid="macro-gallery" />
            <MacroButton icon={Users} label="Friends" onClick={() => setFriendsOpen(true)} testid="macro-friends" />
          </div>
        </section>

        {jimAllowed && (
          <button onClick={() => setAdminOpen(true)} data-testid="profile-admin-launch-btn"
            className="mt-4 w-full flex items-center justify-between border-4 border-gold rounded-[24px] bg-charcoal px-4 py-3.5 shadow-[4px_4px_0px_#D4AF37] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-transform">
            <span className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl border-2 border-gold grid place-items-center bg-white/5"><Settings size={20} strokeWidth={2.2} className="text-gold" /></span>
              <span className="text-left leading-tight">
                <span className="block font-serif text-lg text-white">Admin Panel</span>
                <span className="block font-mono text-[8px] uppercase tracking-[0.18em] text-gold/80">API Credits · Payouts · Engine · Tools</span>
              </span>
            </span>
            <Plus size={18} className="text-gold" />
          </button>
        )}

        {/* COMPOSER ROW — 2/3 width centered */}
        <button onClick={() => setComposer(true)} data-testid="composer-row"
          className="mt-4 mx-auto w-2/3 flex items-center gap-2.5 bg-white border-2 border-charcoal rounded-full pl-1.5 pr-4 py-1.5 shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-[transform,box-shadow] duration-150">
          <span className="w-8 h-8 rounded-full border-2 border-charcoal overflow-hidden bg-gold grid place-items-center shrink-0">
            {avatarSrc ? <img src={avatarSrc} alt="" className="w-full h-full object-cover" />
              : <DefaultAvatar name={user?.name} gender={user?.gender} />}
          </span>
          <span className="font-mono text-[11px] text-charcoal/45 truncate">What's on your mind?</span>
        </button>

        {/* WHAT'S UP — combined reels + posts feed accordion */}
        <ProfileFeed refreshKey={feedKey} open={feedOpen} onToggleOpen={() => setFeedOpen((v) => !v)} />
      </div>
    </div>
  );
}
